import React, { useEffect } from "react";
import {
  Routes,
  Route,
  Navigate,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { SearchProvider } from "./context/searchcontext";
import { AuthProvider } from "./context/authcontext";
import { UIProvider } from "./context/uicontext";
import { useSearch } from "./context/searchcontext";
import { useAuth } from "./context/authcontext";
import Navbar from "./components/navbar";
import Footer from "./components/footer";
import Home from "./components/home";
import Listings from "./components/listings";
import PGDetail from "./components/pgdetail";
import ThankYou from "./components/thankyou";
import About from "./components/aboutNew";
import Contact from "./components/contact";
import OwnerDashboard from "./components/ownerdashboard";
import TenantDashboard from "./components/tenantdashboard";
import TenantFormPage from "./components/TenantFormPage";
import TenantFormPageByBarcodeId from "./components/TenantFormPageByBarcodeId";
import ScrollToTop from "./components/ScrollToTop";
import { isListingSlug } from "./utils/slugutils";
import Blog from "./components/blog";
import Branding from "./components/branding";
import { usePageTracking } from "./hooks/usePageTracking";
import RentReceipt from "./components/rentReceipt";
import AgreementView from "./components/RentAgreementViewDynamic";

const shouldShowFooter = (pathname) =>
  pathname === "/" ||
  pathname === "/listings" ||
  pathname === "/blog" ||
  pathname === "/about" ||
  pathname === "/thank-you" ||
  pathname === "/contact" ||
  isListingSlug(pathname) ||
  pathname.startsWith("/pg/") ||
  pathname.startsWith("/promo/");

// ── Replaces the broken `/pg-*` route ────────────────────────────────────────
const CatchAll = () => {
  const { pathname } = useLocation();
  if (pathname.startsWith("/pg-")) {
    return <Listings />;
  }
  return <Navigate to="/" replace />;
};

// ✅ Protected Route wrapper component
const ProtectedRoute = ({ children, allowedTypes }) => {
  const { user, isLoading } = useAuth(); // ✅ Get isLoading from context
  const navigate = useNavigate();

  useEffect(() => {
    if (isLoading) return; // ✅ Wait for loading to complete

    if (!user) {
      navigate("/", { replace: true });
      return;
    }

    if (allowedTypes && !allowedTypes.includes(user.type)) {
      navigate("/", { replace: true });
    }
  }, [user, navigate, allowedTypes, isLoading]); // ✅ Add isLoading to dependencies

  if (isLoading) return null; // ✅ Show nothing while loading
  if (!user) return null;
  if (allowedTypes && !allowedTypes.includes(user.type)) return null;

  return children;
};

const SiteLayout = () => {
  const { pathname } = useLocation();
  const { isSearching } = useSearch();
  usePageTracking();

  return (
    <>
      <ScrollToTop />
      <Navbar />
      {isSearching && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            background: '#fff',
            zIndex: 500,
          }}
        >
          <img
            src="/assets/aapkapgLoader.gif"
            alt="Loading..."
            style={{ width: 'auto', height: '10vh' }}
          />
        </div>
      )}
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/listings" element={<Listings />} />
        <Route path="/pg/:pgSlug" element={<PGDetail />} />
        <Route path="/thank-you" element={<ThankYou />} />
        <Route path="/about" element={<About />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/blog" element={<Blog />} />
        <Route path="/promo/:branding" element={<Branding />} />
        <Route path="*" element={<CatchAll />} />
      </Routes>
      {shouldShowFooter(pathname) && <Footer />}
    </>
  );
};

function App() {
  return (
    <AuthProvider>
      <UIProvider>
        <SearchProvider>
          <Routes>
            {/* ✅ Protected routes - MUST be defined BEFORE the /* route */}
            <Route 
              path="/owner-dashboard/*" 
              element={
                <ProtectedRoute allowedTypes={['pg']}>
                  <OwnerDashboard />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/tenant-dashboard" 
              element={
                <ProtectedRoute allowedTypes={['tenant']}>
                  <TenantDashboard />
                </ProtectedRoute>
              } 
            />
            
            {/* Public routes */}
            <Route path="/tenant-form/:pgId" element={<TenantFormPage />} />
            <Route path="/agreement/:encryptedParams" element={<AgreementView />} />
            <Route path="/rentreceipt" element={<RentReceipt />} />
            <Route path="/tenant-form-page/:BarcodeId" element={<TenantFormPageByBarcodeId />} />
            
            {/* ✅ Catch-all route - MUST be LAST */}
            <Route path="/*" element={<SiteLayout />} />
          </Routes>
        </SearchProvider>
      </UIProvider>
    </AuthProvider>
  );
}

export default App;